import { useCallback } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

import { expValEquals } from '@confluence/feature-experiments';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import {
	useConvertDialogActions,
	useConvertDialogState,
	type ConvertDialogStateValue,
} from './ConvertDialogStore.ts';
import { countSyncBlocks } from './countSyncBlocks.ts';
import { filterToMarkdownSafeAdf } from './filterToMarkdownSafeAdf.ts';
import { isMarkdownConversionLossy } from './isMarkdownConversionLossy.ts';
import { refetchPageContent } from './refetchPageContent.ts';
import { useIsMarkdownMode } from './useIsMarkdownMode.ts';
import { useMarkdownConvertFlags } from './useMarkdownConvertFlags.tsx';
import { patchIsMarkdownModeCache, useUpdateMarkdownMode } from './useUpdateMarkdownMode.ts';

/**
 * Mirrors `EditorObjectHeaderContext.editorActions`: either the real
 * `EditorActions` class (when the editor is mounted) or a minimal
 * placeholder with just `isDocumentEmpty` (when it isn't).
 */
type DefaultNoop = () => void;
type EmptyEditorActions = {
	isDocumentEmpty: DefaultNoop;
};
export type EditorActionsOrEmpty = EditorActions | EmptyEditorActions;

/** Optional subset of `FlagsStateContainer` we call. `unknown` returns let void-returning stubs satisfy the type. */
export type FlagsLike = {
	[K in 'showErrorFlag' | 'showFlag' | 'hideFlag']?: (
		...args: Parameters<FlagsStateContainer[K]>
	) => unknown;
};

type UseMarkdownModeActionsArgs = {
	contentId: string;
	isLivePage: boolean;
	editorActions: EditorActionsOrEmpty;
	flags: FlagsLike;
};

export type UseMarkdownModeActionsResult = {
	isMarkdownMode: boolean;
	isUpdating: boolean;
	/** Current dialog state (open + direction + lossy) for the conversion confirmation modal. */
	convertDialogState: ConvertDialogStateValue;
	/** Close the conversion confirmation modal. */
	closeConvertDialog: () => void;
	/** True when the doc contains synced blocks and the convert flow is blocked on the user unlinking them first. */
	isSyncBlockBlockerOpen: boolean;
	/** Number of `bodiedSyncBlock` nodes the most recent convert attempt found — drives the pluralised dialog copy. */
	syncBlockCount: number;
	/** Close the unlink-synced-blocks blocker dialog. */
	closeSyncBlockBlocker: () => void;
	/**
	 * Entry-point for "Convert to markdown". Inspects the doc and opens
	 * either the unlink-synced-blocks blocker (if the doc contains any
	 * `bodiedSyncBlock`) or the confirmation dialog with the right
	 * `isLossy` value. Never flips the BE flag directly — that happens
	 * on dialog confirm.
	 */
	convertToMarkdown: () => Promise<void>;
	/**
	 * Entry-point for "Convert to rich text". Always opens the confirmation
	 * dialog (no lossy state — the markdown→rich direction is never lossy).
	 */
	convertToRichText: () => Promise<void>;
	/** Called from the dialog when user confirms a `toMarkdown` conversion. */
	confirmConvertToMarkdown: () => Promise<{ success: boolean }>;
	/** Called from the dialog when user confirms a `toRichText` conversion. */
	confirmConvertToRichText: () => Promise<{ success: boolean }>;
};

/**
 * Markdown-mode toggle flow for a live page. Reads current mode + mutation
 * state, drains in-flight collab steps before the BE flag flip (see
 * `drainUnconfirmedSteps`), and on confirm runs the
 * refetch → filter → clear → replaceDocument → patch sequence that keeps
 * the editor populated across the schema switch (see `flipFlagToMarkdown`).
 */
export const useMarkdownModeActions = ({
	contentId,
	isLivePage,
	editorActions,
	flags,
}: UseMarkdownModeActionsArgs): UseMarkdownModeActionsResult => {
	const { updateMarkdownMode, loading: isUpdating } = useUpdateMarkdownMode();
	const isMarkdownMode = useIsMarkdownMode(isLivePage ? contentId : undefined);
	// Dialog open/close lives in a sweet-state store so multiple call sites
	// (header overflow menu, title `.md`-suffix gesture, `cmd+shift+x`
	// shortcut) all drive the same dialog instance rendered by `MoreActions`.
	const [{ dialog, syncBlockCount }] = useConvertDialogState();
	const isSyncBlockBlockerOpen = syncBlockCount > 0;
	const { openConvertDialog, closeConvertDialog, openSyncBlockBlocker, closeSyncBlockBlocker } =
		useConvertDialogActions();
	const convertFlags = useMarkdownConvertFlags(flags);

	// Wait for any unconfirmed collab steps to be acked before flipping the
	// markdown-mode flag. The convert mutation invalidates the live-doc
	// session on the BE, which triggers an NCS restore that would otherwise
	// re-apply our unconfirmed steps on top of the server doc that already
	// has them — duplicating anything typed just before the click.
	const drainUnconfirmedSteps = useCallback(async () => {
		if (!('getResolvedEditorState' in editorActions)) {
			return;
		}
		try {
			await editorActions.getResolvedEditorState('publish');
		} catch {
			// Swallow — don't block convert if the drain fails.
		}
	}, [editorActions]);

	const flipFlagToMarkdown = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, true);
		if (!result.success) {
			convertFlags.showError('toMarkdown');
			return { success: false };
		}

		// Replace the PM doc with the markdown-safe BE body BEFORE flipping
		// the mode cache. The flip triggers `reconfigureState`, which migrates
		// the current doc via `PMNode.fromJSON(markdownSchema, ...)` — any
		// rich-text-only node still in the doc (`status`, `panel`, `expand`,
		// ...) throws and resets the editor to empty.
		//
		// Monolith holds the rich-text ADF; NCS catchupv2 delivers the
		// filtered version. Running `filterToMarkdownSafeAdf` here matches
		// what NCS would deliver without FE/BE divergence (same pipeline
		// filter).
		const convertedBody = await refetchPageContent(contentId);
		if (convertedBody && 'replaceDocument' in editorActions) {
			try {
				const parsed: unknown = JSON.parse(convertedBody);
				const safe = filterToMarkdownSafeAdf(parsed);
				if (safe) {
					editorActions.replaceDocument(safe, false);
				}
			} catch {
				// Editor unmounted, JSON parse failed, or filter rejected the
				// input. The flip still proceeds; NCS catchupv2 recovers.
			}
		}

		patchIsMarkdownModeCache(contentId, true, expValEquals('cc-markdown-mode', 'isEnabled', true));
		convertFlags.showSuccess('toMarkdown');
		return { success: true };
	}, [contentId, convertFlags, drainUnconfirmedSteps, editorActions, updateMarkdownMode]);

	const flipFlagToRichText = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, false);
		if (!result.success) {
			convertFlags.showError('toRichText');
			return { success: false };
		}
		// Rich-text schema is a superset of markdown, so the `reconfigureState`
		// migration always succeeds — no doc replace needed. Refetch still
		// runs so the `CollabDraftQuery` cache mirrors the BE body and the
		// dialog holds in `isSubmitting` for the round-trip.
		await refetchPageContent(contentId);
		patchIsMarkdownModeCache(contentId, false, expValEquals('cc-markdown-mode', 'isEnabled', true));
		convertFlags.showSuccess('toRichText');
		return { success: true };
	}, [contentId, convertFlags, drainUnconfirmedSteps, updateMarkdownMode]);

	const confirmConvertToMarkdown = useCallback(() => flipFlagToMarkdown(), [flipFlagToMarkdown]);

	const confirmConvertToRichText = useCallback(async () => {
		return flipFlagToRichText();
	}, [flipFlagToRichText]);

	// Synced blocks fan out to other pages and have no markdown
	// representation, so a doc containing any `bodiedSyncBlock` opens the
	// unlink-first blocker instead of the convert dialog — there is no
	// "convert anyway" path.
	const convertToMarkdown = useCallback(async () => {
		let adf: unknown;
		if ('getValue' in editorActions) {
			try {
				adf = await editorActions.getValue();
			} catch {
				adf = undefined;
			}
		}

		const syncBlockNodeCount = countSyncBlocks(adf);
		if (syncBlockNodeCount > 0) {
			openSyncBlockBlocker(syncBlockNodeCount);
			return;
		}

		openConvertDialog({
			direction: 'toMarkdown',
			isLossy: await isMarkdownConversionLossy(adf, contentId),
		});
	}, [contentId, editorActions, openConvertDialog, openSyncBlockBlocker]);

	const convertToRichText = useCallback(async () => {
		openConvertDialog({ direction: 'toRichText' });
	}, [openConvertDialog]);

	return {
		isMarkdownMode,
		isUpdating,
		convertDialogState: dialog,
		closeConvertDialog,
		isSyncBlockBlockerOpen,
		syncBlockCount,
		closeSyncBlockBlocker,
		convertToMarkdown,
		convertToRichText,
		confirmConvertToMarkdown,
		confirmConvertToRichText,
	};
};
